import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  AtSign,
  BadgeCheck,
  Ban,
  BarChart3,
  Bot,
  ChevronLeft,
  Crown,
  Flag,
  Image,
  LinkIcon,
  MessageSquareText,
  MessagesSquare,
  Rocket,
  ScrollText,
  ShieldCheck,
  SlidersHorizontal,
  TrendingUp,
  UserPlus,
  Users,
} from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchGroup, type AdminGroupDetail } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { InfoRow, MonoValue, RelativeTime } from '@/components/DataDisplay'
import { StatusBadge, Tag } from '@/components/StatusBadge'
import { ErrorState, PermissionDenied, Skeleton } from '@/components/States'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'
import { setGroupVerified, transferGroupOwner, releaseGroupUsername, setGroupBlocked } from './groupVerifyApi'

/**
 * A4.04 — Chat kartasi (umumiy). GET /admin/groups/:id (backend `25f5992`).
 */
export function GroupCard() {
  const t = useT()
  const { can } = useAuth()
  const { id = '' } = useParams()
  const [group, setGroup] = useState<AdminGroupDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  const { toast } = useToast()
  const [verifying, setVerifying] = useState(false)

  // A4.29 — verifikatsiya belgisini yoq/o'chir (bot verifikatsiyasi kabi).
  const toggleVerify = async () => {
    if (!group) return
    const next = !group.verified
    setVerifying(true)
    try {
      await setGroupVerified(group.id, next)
      toast(next ? 'Verifikatsiya berildi' : 'Verifikatsiya olindi', { tone: 'success' })
      setReload((n) => n + 1)
    } catch (e) {
      toast('Amal bajarilmadi', { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    } finally {
      setVerifying(false)
    }
  }

  // A4.28 — egalikni yangi egaga o'tkazish. Yangi ega ID'sini so'raydi (a'zolar
  // ro'yxatidan olinadi); backend uni faol a'zo ekanini tekshiradi.
  const [transferring, setTransferring] = useState(false)
  const doTransfer = async () => {
    if (!group) return
    const raw = (window.prompt("Yangi ega — foydalanuvchi ID (guruh a'zosi bo'lishi shart):") ?? '').trim()
    if (!raw) return
    const newOwner = Number(raw)
    if (!Number.isInteger(newOwner) || newOwner <= 0) {
      toast("ID noto'g'ri", { tone: 'warning' })
      return
    }
    if (!window.confirm(`Egalik user #${newOwner} ga o'tkazilsinmi? Eski ega adminga tushiriladi.`)) return
    setTransferring(true)
    try {
      await transferGroupOwner(group.id, newOwner)
      toast('Egalik o‘tkazildi', { tone: 'success' })
      setReload((n) => n + 1)
    } catch (e) {
      toast('Amal bajarilmadi', { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    } finally {
      setTransferring(false)
    }
  }

  // A4.30 — @username'ni majburan bo'shatish (skvatting/haqoratli username).
  const [releasing, setReleasing] = useState(false)
  const doReleaseUsername = async () => {
    if (!group?.username) return
    if (!window.confirm(`@${group.username} bo'shatilsinmi? Username qaytadan olinishi mumkin bo'ladi.`)) return
    setReleasing(true)
    try {
      const freed = await releaseGroupUsername(group.id)
      toast(`@${freed} bo‘shatildi`, { tone: 'success' })
      setReload((n) => n + 1)
    } catch (e) {
      toast('Amal bajarilmadi', { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    } finally {
      setReleasing(false)
    }
  }

  // A4.27 — guruh/kanalni bloklash/ochish. Bloklanganda hech kim (ega ham) yozolmaydi.
  const [blocking, setBlocking] = useState(false)
  const toggleBlock = async () => {
    if (!group) return
    const next = !group.admin_blocked
    let reason = ''
    if (next) {
      reason = (window.prompt('Bloklash sababi (ixtiyoriy):') ?? '').trim()
      if (!window.confirm('Guruh/kanal bloklansinmi? Hech kim (ega ham) yangi xabar yozolmaydi.')) return
    }
    setBlocking(true)
    try {
      await setGroupBlocked(group.id, next, reason)
      toast(next ? 'Bloklandi' : 'Blok olindi', { tone: 'success' })
      setReload((n) => n + 1)
    } catch (e) {
      toast('Amal bajarilmadi', { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    } finally {
      setBlocking(false)
    }
  }

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchGroup(Number(id), ac.signal)
      .then((g) => {
        if (alive) setGroup(g)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [id, reload])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  return (
    <>
      <PageHeader
        title={group ? group.title || (group.username ? `@${group.username}` : `#${group.id}`) : t.groups.cardTitle}
        screenId="A4.04"
        actions={
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => void toggleVerify()}
              disabled={verifying || !group}
              className={
                group?.verified
                  ? 'inline-flex items-center gap-1.5 rounded-md border border-sky-500/40 bg-sky-500/10 px-2.5 py-1 text-sm font-medium text-sky-300 hover:bg-sky-500/20 disabled:opacity-50'
                  : 'inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary disabled:opacity-50'
              }
            >
              <BadgeCheck size={13} />
              {group?.verified ? 'Verifikatsiyani olish' : 'Verifikatsiya berish'}
            </button>
            <button
              onClick={() => void toggleBlock()}
              disabled={blocking || !group}
              className={
                group?.admin_blocked
                  ? 'inline-flex items-center gap-1.5 rounded-md border border-rose-500/50 bg-rose-500/15 px-2.5 py-1 text-sm font-medium text-rose-300 hover:bg-rose-500/25 disabled:opacity-50'
                  : 'inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-rose-500/40 hover:text-rose-300 disabled:opacity-50'
              }
            >
              <Ban size={13} />
              {group?.admin_blocked ? 'Blokni ochish' : 'Bloklash'}
            </button>
            <button
              onClick={() => void doTransfer()}
              disabled={transferring || !group}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary disabled:opacity-50"
            >
              <Crown size={13} />
              Egalik o‘tkazish
            </button>
            {group?.username ? (
              <button
                onClick={() => void doReleaseUsername()}
                disabled={releasing}
                className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-sm text-amber-300 hover:border-amber-500/60 hover:text-amber-200 disabled:opacity-50"
              >
                <AtSign size={13} />
                Username bo‘shatish
              </button>
            ) : null}
            <Link
              to={`/groups/${id}/messages`}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <MessageSquareText size={13} />
              {t.groups.messages}
            </Link>
            <Link
              to={`/groups/${id}/media`}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <Image size={13} />
              {t.groups.media}
            </Link>
            <Link
              to={`/groups/${id}/members`}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <Users size={13} />
              {t.groups.members}
            </Link>
            <Link
              to={`/groups/${id}/admins`}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <ShieldCheck size={13} />
              {t.groups.admins}
            </Link>
            <Link
              to={`/groups/${id}/bots`}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <Bot size={13} />
              {t.groups.botsTitle}
            </Link>
            <Link
              to={`/groups/${id}/settings`}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <SlidersHorizontal size={13} />
              {t.groups.settingsTitle}
            </Link>
            <Link
              to={`/groups/${id}/reports`}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <Flag size={13} />
              {t.groups.reportsTitle}
            </Link>
            <Link
              to={`/groups/${id}/invites`}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <LinkIcon size={13} />
              {t.invites.title}
            </Link>
            <Link
              to={`/groups/${id}/join-requests`}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <UserPlus size={13} />
              {t.groups.joinRequests}
            </Link>
            <Link
              to={`/groups/${id}/stats`}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <BarChart3 size={13} />
              {t.groups.stats}
            </Link>
            <Link
              to={`/groups/${id}/growth`}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <TrendingUp size={13} />
              {t.groups.growth}
            </Link>
            <Link
              to={`/groups/${id}/boosts`}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <Rocket size={13} />
              {t.groups.boosts}
            </Link>
            <Link
              to={`/groups/${id}/admin-log`}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <ScrollText size={13} />
              {t.groups.adminLog}
            </Link>
            {group?.is_forum && (
              <Link
                to={`/groups/${id}/topics`}
                className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
              >
                <MessagesSquare size={13} />
                {t.groups.topics}
              </Link>
            )}
            <Link
              to="/groups"
              className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
            >
              <ChevronLeft size={13} />
              {t.nav.groups}
            </Link>
          </div>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="max-w-2xl space-y-2 rounded-lg border border-border-default bg-surface-2 p-4">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-4 w-2/3" />
            ))}
          </div>
        ) : error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : !group ? (
          <ErrorState title={t.states.notFoundTitle} />
        ) : (
          <div className="max-w-2xl rounded-lg border border-border-default bg-surface-2 p-4">
            <div className="mb-3 flex items-center gap-2">
              <StatusBadge
                status={group.type === 'channel' ? 'review' : 'active'}
                label={group.type === 'channel' ? t.groups.channel : t.groups.group}
              />
              {group.is_forum && <Tag>{t.groups.forum}</Tag>}
            </div>
            <InfoRow label={t.col.id}>
              <MonoValue value={String(group.id)} />
            </InfoRow>
            <InfoRow label={t.col.title}>{group.title || '—'}</InfoRow>
            <InfoRow label={t.col.username}>{group.username ? `@${group.username}` : '—'}</InfoRow>
            <InfoRow label={t.groups.about}>{group.about || '—'}</InfoRow>
            <InfoRow label={t.groups.members}>
              <span className="font-mono tabular">{group.member_count.toLocaleString()}</span>
            </InfoRow>
            <InfoRow label={t.groups.owner}>
              {group.owner_id ? (
                <Link to={`/users/${group.owner_id}`} className="text-accent hover:underline">
                  {group.owner_username ? `@${group.owner_username}` : `#${group.owner_id}`}
                </Link>
              ) : (
                '—'
              )}
            </InfoRow>
            <InfoRow label={t.col.registered}>
              <RelativeTime value={group.created_at} />
            </InfoRow>
          </div>
        )}
      </div>
    </>
  )
}
